import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { ArrowLeft, Check, ChevronRight, TriangleAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Button } from "@babackapp/ui/components/button";
import { cn } from "@babackapp/ui/lib/utils";
import type { TriggerNodeDataDto } from "@babackapp/private-api-client/types";
import {
  TRACKING_STATUS_CATEGORIES,
  TRACKING_STATUS_CATEGORY_COLORS,
  getTrackingStatusesByCategory,
  compareTrackingStatusOrder,
  type TrackingStatus,
  type TrackingStatusCategory,
} from "@babackapp/tracking-status-registry";
import {
  DEFAULT_TRIGGER_CONFIGS,
  RETURN_TRIGGER_TYPE_OPTIONS,
  TRIGGER_TYPE_OPTIONS,
} from "../nodes/trigger/constants";
import { useTrackingStatusMap } from "../nodes/trigger";
import {
  NodeColorKey,
  nodeColorToTailwindClasses,
} from "../lib/node-color-to-tailwind-classes";
import { useWorkflowsEditorConfig } from "../context/workflows-editor-config-context";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { Workflow } from "../types";
import { useTriggerLabel } from "./use-trigger-label";

type TriggerType = TriggerNodeDataDto["triggerType"];
type TriggerConfig = TriggerNodeDataDto["config"];

// Click & collect statuses exposed to merchants (mirrors panel-click-and-collect-status).
const CLICK_AND_COLLECT_STATUSES = ["PRETE_AU_RETRAIT", "RETIREE"] as const;
// Claim statuses (mirrors panel-claim-status).
const CLAIM_STATUSES = [
  "NEW",
  "IN_PROGRESS",
  "RESOLVED",
  "REJECTED",
  "CLOSED",
] as const;

// Trigger types whose config requires a second selection step (choose the
// status/event). Types absent from this set are created immediately when
// picked at level 1.
const TRIGGERS_WITH_DETAIL: ReadonlySet<string> = new Set([
  "tracking_status_change",
  "click_and_collect_status_change",
  "claim_status_change",
]);

interface TriggerTypeOption {
  triggerType: TriggerType;
  title: string;
  description: string;
  hasDetail: boolean;
  /** When true the option is shown but cannot be selected (feature off for shop). */
  disabled: boolean;
  defaultConfig: TriggerConfig;
}

interface DetailOption {
  key: string;
  config: TriggerConfig;
  label: string;
  description?: string;
}

interface CreateWorkflowModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflows: Workflow[];
  onCreate: (triggerType: TriggerType, config: TriggerConfig) => void;
}

export function CreateWorkflowModal({
  open,
  onOpenChange,
  workflows,
  onCreate,
}: CreateWorkflowModalProps) {
  const { t } = useTranslation("trackoffice");
  const getTriggerLabel = useTriggerLabel();
  const statusMap = useTrackingStatusMap();
  const { data: shop } = useCurrentShopSuspense();
  const { availableTriggerTypes } = useWorkflowsEditorConfig();
  const hasClickAndCollect = shop.plan?.trackingClickAndCollect === true;
  const hasClaims = shop.plan?.trackingCarrierClaim === true;

  const [step, setStep] = useState<"type" | "detail">("type");
  const [activeType, setActiveType] = useState<TriggerType | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Level 1 — the trigger types themselves. Tracking types first, then the
  // return-domain types (the type IS the event: empty config, no detail
  // step). Only the current domain's triggers are offered.
  const typeOptions = useMemo<TriggerTypeOption[]>(() => {
    return [...TRIGGER_TYPE_OPTIONS, ...RETURN_TRIGGER_TYPE_OPTIONS]
      .filter((type) => availableTriggerTypes.includes(type))
      .map((type) => {
        const disabled =
          (type === "click_and_collect_status_change" &&
            !hasClickAndCollect) ||
          (type === "claim_status_change" && !hasClaims);
        return {
          triggerType: type as TriggerType,
          title: t(`workflowsEditor.triggerTypes.${type}.title`),
          description: t(`workflowsEditor.triggerTypes.${type}.description`),
          hasDetail: TRIGGERS_WITH_DETAIL.has(type),
          disabled,
          defaultConfig: DEFAULT_TRIGGER_CONFIGS[
            type as keyof typeof DEFAULT_TRIGGER_CONFIGS
          ] as unknown as TriggerConfig,
        };
      });
  }, [t, hasClickAndCollect, hasClaims, availableTriggerTypes]);

  // Level 2 — flat detail options (click & collect / claim).
  const detailOptions = useMemo<DetailOption[]>(() => {
    if (activeType === "click_and_collect_status_change") {
      return CLICK_AND_COLLECT_STATUSES.map((status) => ({
        key: `click_and_collect_status_change:${status}`,
        config: { status } as TriggerConfig,
        label: t(
          `click-and-collect.status.${status.toLowerCase().replace(/_/g, "-")}`,
          status,
        ),
      }));
    }
    if ((activeType as string) === "claim_status_change") {
      return CLAIM_STATUSES.map((status) => ({
        key: `claim_status_change:${status}`,
        config: { claimStatuses: [status] } as unknown as TriggerConfig,
        label: t(`workflowsEditor.claimStatus.${status.toLowerCase()}`, status),
      }));
    }
    return [];
  }, [activeType, t]);

  // Level 2 — tracking statuses grouped by category (mirrors the parcel-page
  // status picker: one coloured group header per category, sorted statuses).
  const trackingStatusGroups = useMemo(() => {
    return TRACKING_STATUS_CATEGORIES.map((category) => {
      const statuses = (
        getTrackingStatusesByCategory(
          category.id as TrackingStatusCategory,
        ).sort(compareTrackingStatusOrder) as TrackingStatus[]
      ).filter((status) => status in statusMap);

      return {
        id: category.id,
        label: category.label,
        color: TRACKING_STATUS_CATEGORY_COLORS[
          category.id as TrackingStatusCategory
        ],
        options: statuses.map<DetailOption>((status) => {
          const option = statusMap[status as keyof typeof statusMap];
          return {
            key: `tracking_status_change:${status}`,
            config: { trackingStatus: status } as TriggerConfig,
            label: option?.label ?? status,
            description: option?.description,
          };
        }),
      };
    }).filter((group) => group.options.length > 0);
  }, [statusMap]);

  // Only needed to render the icon of the flat (click & collect / claim)
  // detail cards; the grouped tracking-status cards don't use it.
  const activeTypeLabel =
    activeType && activeType !== "tracking_status_change"
      ? getTriggerLabel({
        id: "",
        type: "trigger",
        data: {
          label: "trigger",
          triggerType: activeType,
          config: DEFAULT_TRIGGER_CONFIGS[
            activeType as keyof typeof DEFAULT_TRIGGER_CONFIGS
          ] as unknown as TriggerConfig,
        },
      } as Workflow["trigger"])
    : null;

  // Count existing workflows matching a trigger type, optionally narrowed to a
  // specific config (status/event). Level 1 uses the type-only count for
  // triggers without a detail step; level 2 uses the per-config count.
  const matchCount = (triggerType: TriggerType, config?: TriggerConfig) =>
    workflows.filter((w) => {
      const data = w.trigger.data;
      if ((data.triggerType as string) !== (triggerType as string))
        return false;
      if (!config) return true;
      if (
        triggerType === "tracking_status_change" &&
        "trackingStatus" in data.config &&
        "trackingStatus" in config
      ) {
        return data.config.trackingStatus === config.trackingStatus;
      }
      if (
        triggerType === "click_and_collect_status_change" &&
        "status" in data.config &&
        "status" in config
      ) {
        return (
          data.config.status === (config as { status?: string }).status
        );
      }
      if ((triggerType as string) === "claim_status_change") {
        const dataStatuses =
          "claimStatuses" in data.config &&
          Array.isArray(
            (data.config as { claimStatuses?: unknown }).claimStatuses,
          )
            ? (data.config as { claimStatuses: string[] }).claimStatuses
            : [];
        const optionStatuses = (config as { claimStatuses?: string[] })
          .claimStatuses;
        return (
          !!optionStatuses &&
          optionStatuses.every((s) => dataStatuses.includes(s)) &&
          dataStatuses.length === optionStatuses.length
        );
      }
      return true;
    }).length;

  const resetState = () => {
    setStep("type");
    setActiveType(null);
    setSelectedKey(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetState();
    onOpenChange(next);
  };

  const handleTypeClick = (option: TriggerTypeOption) => {
    if (option.disabled) return;
    if (option.hasDetail) {
      setActiveType(option.triggerType);
      setSelectedKey(null);
      setStep("detail");
      return;
    }
    // No detail to pick — create straight away.
    onCreate(option.triggerType, option.defaultConfig);
    handleOpenChange(false);
  };

  const handleBack = () => {
    setStep("type");
    setActiveType(null);
    setSelectedKey(null);
  };

  const handleConfirmDetail = () => {
    if (!activeType || !selectedKey) return;
    const option = [
      ...detailOptions,
      ...trackingStatusGroups.flatMap((group) => group.options),
    ].find((o) => o.key === selectedKey);
    if (!option) return;
    onCreate(activeType, option.config);
    handleOpenChange(false);
  };

  const renderDetailCard = (
    option: DetailOption,
    icon?: { Icon: LucideIcon; accent: NodeColorKey },
  ) => {
    const isSelected = selectedKey === option.key;
    const count = activeType ? matchCount(activeType, option.config) : 0;
    const palette = icon ? nodeColorToTailwindClasses(icon.accent) : null;
    const Icon = icon?.Icon;

    return (
      <button
        key={option.key}
        type="button"
        onClick={() => setSelectedKey(option.key)}
        className={cn(
          "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
          isSelected ? "border-primary bg-primary/5" : "hover:bg-accent",
        )}
      >
        {Icon && palette && (
          <span
            className={cn(
              "flex size-9 shrink-0 items-center justify-center rounded-md",
              palette.background,
              palette.text,
            )}
          >
            <Icon className="size-4" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{option.label}</span>
          {option.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {option.description}
            </p>
          )}
        </div>
        {count > 0 && (
          <span className="mt-0.5 shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
            {t("workflowsEditor.createModal.existingCount", "{count} workflow(s)", {
              count,
            })}
          </span>
        )}
        {isSelected && (
          <Check className="mt-0.5 size-4 shrink-0 text-primary" />
        )}
      </button>
    );
  };

  const isDetailStep = step === "detail";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDetailStep && (
              <button
                type="button"
                onClick={handleBack}
                aria-label={t("workflowsEditor.createModal.back", "Back")}
                className="-ml-1 flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            {isDetailStep && activeType
              ? t(`workflowsEditor.triggerTypes.${activeType}.title`)
              : t("workflowsEditor.createModal.title", "Choose a trigger")}
          </DialogTitle>
          <DialogDescription>
            {isDetailStep
              ? t(
                  "workflowsEditor.createModal.detailDescription",
                  "Pick the detail that fires this workflow.",
                )
              : t(
                  "workflowsEditor.createModal.description",
                  "Select the event that starts this workflow. You can pick only one.",
                )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto py-1 pr-1">
          {!isDetailStep &&
            typeOptions.map((option) => {
              const { Icon, accent } = getTriggerLabel({
                id: "",
                type: "trigger",
                data: {
                  label: "trigger",
                  triggerType: option.triggerType,
                  config: option.defaultConfig,
                },
              } as Workflow["trigger"]);
              const palette = nodeColorToTailwindClasses(accent);
              const isDisabled = option.disabled;
              // Aggregate count only for triggers without a detail step; the
              // detailed ones show per-status counts on level 2.
              const count = option.hasDetail
                ? 0
                : matchCount(option.triggerType);

              return (
                <button
                  key={option.triggerType}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleTypeClick(option)}
                  className={cn(
                    "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                    isDisabled
                      ? "cursor-not-allowed opacity-50"
                      : "hover:bg-accent",
                  )}
                >
                  <span
                    className={cn(
                      "flex size-9 shrink-0 items-center justify-center rounded-md",
                      palette.background,
                      palette.text,
                    )}
                  >
                    <Icon className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">
                      {option.title}
                    </span>
                    {option.description && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {option.description}
                      </p>
                    )}
                  </div>
                  {isDisabled ? (
                    <span className="mt-0.5 shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                      {t(
                        "workflowsEditor.createModal.featureDisabled",
                        "Not on your plan",
                      )}
                    </span>
                  ) : (
                    <>
                      {count > 0 && (
                        <span className="mt-0.5 shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                          {t(
                            "workflowsEditor.createModal.existingCount",
                            "{count} workflow(s)",
                            { count },
                          )}
                        </span>
                      )}
                      {option.hasDetail && (
                        <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      )}
                    </>
                  )}
                </button>
              );
            })}

          {isDetailStep &&
            activeType === "tracking_status_change" &&
            trackingStatusGroups.map((group) => (
              <div key={group.id} className="flex flex-col gap-2">
                <div className="flex items-center gap-2 px-1 pt-1 text-xs font-semibold text-muted-foreground">
                  <span
                    className="inline-block size-2 rounded-full"
                    style={{ backgroundColor: group.color }}
                  />
                  {group.label}
                </div>
                {group.options.map((option) => renderDetailCard(option))}
              </div>
            ))}

          {isDetailStep &&
            activeType !== "tracking_status_change" &&
            detailOptions.map((option) =>
              renderDetailCard(
                option,
                activeTypeLabel
                  ? { Icon: activeTypeLabel.Icon, accent: activeTypeLabel.accent }
                  : undefined,
              ),
            )}
        </div>

        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-400">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span>
            {t(
              "workflowsEditor.createModal.triggerLockWarning",
              "You can't change the trigger type after creation, but you can still adjust its settings and add actions.",
            )}
          </span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t("workflowsEditor.listing.deleteDialog.cancel", "Cancel")}
          </Button>
          {isDetailStep && (
            <Button onClick={handleConfirmDetail} disabled={!selectedKey}>
              {t("workflowsEditor.createModal.create", "Create workflow")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
